import React, { useEffect, useMemo, useState } from 'react';
import PageLayout from '../../layouts/PageLayout';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { companyHasSinglePermission, userHasSinglePermission } from 'util/PermissionsUtils';
import { Permission } from 'common/models/Permissions';
import PageContentLayout from 'layouts/PageContentLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GetSettingBySettingName } from 'services/UsersService';
import ActivitiesGridComponent from 'components/Grids/ActivitiesGrid';
import Snackbar from '@mui/material/Snackbar';
import Alert from 'components/Alert';
import MeetingsGridComponent from 'components/Grids/MeetingsGrid';
import OpportunitiesGridComponent from 'components/Grids/OpportunitiesGrid';
import AdvertsGridComponent from 'components/Grids/AdvertsGrid';
import PlacementsGridComponent from 'components/Grids/PlacementsGrid';
import MainDashboardLayout from 'components/Dashboards/MainDashboardLayout';
import Box from '@mui/material/Box';
import TimezoneVerificationDialog from 'components/Dialogs/TimezoneVerificationDialog';
import HomeOpenJobsTabContent from 'components/Content/HomeOpenJobsTabContent';

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('Home');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingTab, setIsLoadingTab] = useState(false);
  const [isTimeZoneLoading, setIsTimeZoneLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [tabAction, setTabAction] = useState<JSX.Element>();
  const [isFinishedSetup, setIsFinishedSetup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Home | Recruit Wizard';
  }, []);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const getData = async () => {
      setIsLoading(true);
      if (!tabParam) {
        const tabSetting = await GetSettingBySettingName('DefaultTabDashboard');
        if (tabSetting) setActiveTab(tabSetting);
      } else setActiveTab(tabParam);
      setIsFinishedSetup(true);
      setIsLoading(false);
    };
    getData();
  }, [searchParams]);

  const isActivitiesAvailable = useMemo(
    () =>
      userHasSinglePermission(Permission.Activities) &&
      companyHasSinglePermission(Permission.Activities),
    []
  );
  const isJobsAvailable = useMemo(
    () => userHasSinglePermission(Permission.Jobs) && companyHasSinglePermission(Permission.Jobs),
    []
  );
  const isAdvertsAvailable = useMemo(
    () =>
      userHasSinglePermission(Permission.Adverts) && companyHasSinglePermission(Permission.Adverts),
    []
  );

  const isContractorsAvailable = useMemo(
    () =>
      userHasSinglePermission(Permission.Placements) &&
      companyHasSinglePermission(Permission.Placements),
    []
  );
  const isOpportunitiesAvailable = useMemo(
    () =>
      userHasSinglePermission(Permission.Opportunities) &&
      companyHasSinglePermission(Permission.Opportunities),
    []
  );

  const handleTabChange = (e: React.SyntheticEvent, newValue: string) => {
    navigate(`/home?tab=${newValue}`);
  };

  useEffect(() => {
    if (tabAction && activeTab !== 'Home') {
      setTabAction(undefined);
    }
  }, [activeTab, tabAction]);

  return (
    <PageLayout paddingTop={0}>
      <Snackbar
        open={successMessage !== ''}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
      </Snackbar>
      <Snackbar open={errorMessage !== ''} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <TimezoneVerificationDialog loadingHandler={setIsTimeZoneLoading} />
      <Box display="flex">
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ flexGrow: 1 }}>
          <Tab value="Home" label="Dashboard" />
          {isJobsAvailable && <Tab value="Jobs" label="Open Jobs" />}
          {isActivitiesAvailable && <Tab value="Activities" label="Open Activity" />}
          {isActivitiesAvailable && <Tab value="Meetings" label="Open Meetings" />}
          {isOpportunitiesAvailable && <Tab value="Opportunities" label="Open Opportunities" />}
          {isAdvertsAvailable && <Tab value="Adverts" label="Live Adverts" />}
          {isContractorsAvailable && <Tab value="Contractors" label="Contractors" />}
        </Tabs>
        <Box alignSelf="center">{tabAction}</Box>
      </Box>
      <PageContentLayout showLoading={isLoading || isLoadingTab || isTimeZoneLoading}>
        {isFinishedSetup && activeTab === 'Home' && (
          <MainDashboardLayout
            homeDashboard={true}
            loadingHandler={setIsLoadingTab}
            errorHandler={setErrorMessage}
            setActionHandler={setTabAction}
            renderDashboardDropDown={true}
          />
        )}
        {isFinishedSetup && isJobsAvailable && activeTab === 'Jobs' && (
          <HomeOpenJobsTabContent loadingHandler={setIsLoadingTab} />
        )}
        {isFinishedSetup && isActivitiesAvailable && activeTab === 'Activities' && (
          <ActivitiesGridComponent
            source="dashboard"
            loadingHandler={setIsLoadingTab}
            errorHandler={setErrorMessage}
            successHandler={setSuccessMessage}
            checkboxSelection
            gridName="dashboard/Activities"
          />
        )}
        {isFinishedSetup && isActivitiesAvailable && activeTab === 'Meetings' && (
          <MeetingsGridComponent
            source="dashboard"
            loadingHandler={setIsLoadingTab}
            errorHandler={setErrorMessage}
            successHandler={setSuccessMessage}
            gridName="dashboard/Meetings"
          />
        )}
        {isFinishedSetup && isOpportunitiesAvailable && activeTab === 'Opportunities' && (
          <OpportunitiesGridComponent
            source="dashboard"
            loadingHandler={setIsLoadingTab}
            errorHandler={setErrorMessage}
            successHandler={setSuccessMessage}
            gridName="dashboard/Opportunities"
          />
        )}
        {isFinishedSetup && isAdvertsAvailable && activeTab === 'Adverts' && (
          <AdvertsGridComponent
            loadingHandler={setIsLoadingTab}
            errorHandler={setErrorMessage}
            successHandler={setSuccessMessage}
            gridName="dashboard/Adverts"
            source="dashboard"
          />
        )}
        {isFinishedSetup && isContractorsAvailable && activeTab === 'Contractors' && (
          <PlacementsGridComponent
            source="dashboard"
            loadingHandler={setIsLoadingTab}
            errorHandler={setErrorMessage}
            successHandler={setSuccessMessage}
            gridName="dashboard/Contractors"
          />
        )}
      </PageContentLayout>
    </PageLayout>
  );
}
